import { useState } from 'react';
import {
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  Users,
  Mail,
  AlertCircle,
  CheckCircle2,
  AlertOctagon,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useSafetyStatus,
  useSafetyControls,
  useRecentAnomalies,
} from '@/hooks/useEmailBackend';

export function SafetyAlertsPanel() {
  const [selectedTab, setSelectedTab] = useState('status');

  const { data: safetyStatus } = useSafetyStatus();
  const { data: safetyControls } = useSafetyControls();
  const { data: recentAnomalies } = useRecentAnomalies(24);

  const status = safetyStatus?.data;
  // useSafetyControls returns both active and inactive controls (history is
  // surfaced by SafetyControlsManager). This panel's "Active Controls" tab
  // only renders currently-active ones, so filter here.
  // Normalize control_type → type (DB/API uses control_type; UI reads .type),
  // else `.replace(...)` crashes this panel when a control exists.
  const controls = (safetyControls?.data || [])
    .map((c: any) => ({ ...c, type: c.type ?? c.control_type }))
    .filter((c: any) => c.is_active);
  const anomalies = recentAnomalies?.data || [];

  const getStatusIcon = (systemStatus: string) => {
    switch (systemStatus) {
      case 'operational': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'degraded': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'maintenance': return <Clock className="h-5 w-5 text-blue-500" />;
      case 'emergency': return <AlertOctagon className="h-5 w-5 text-red-500" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (systemStatus: string) => {
    switch (systemStatus) {
      case 'operational': return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'maintenance': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'emergency': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertOctagon className="h-4 w-4" />;
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      case 'medium': return <AlertCircle className="h-4 w-4" />;
      case 'low': return <Info className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Operator-console banner */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Read-only mode</AlertTitle>
        <AlertDescription>
          Safety controls are managed via the operator console. Contact ops if
          you need to trigger or lift a control.
        </AlertDescription>
      </Alert>

      {/* System Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Email System Safety Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status && (() => {
            // system_status can be missing when the safety-status query returns
            // no row (e.g. a brand-new workspace). Coalesce so the Admin tab
            // doesn't crash on `.charAt` of undefined. Mirrors SafetyValidationPanel.
            const systemStatus = status.system_status || 'unknown';
            return (
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${getStatusColor(systemStatus)}`}>
              {getStatusIcon(systemStatus)}
              <div>
                <div className="font-medium">
                  System Status: {systemStatus.charAt(0).toUpperCase() + systemStatus.slice(1)}
                </div>
                <div className="text-sm opacity-75">
                  {status.is_healthy ? 'All systems operational' : 'Issues detected'}
                </div>
              </div>
            </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Detailed Safety Information */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="controls">Active Controls</TabsTrigger>
          <TabsTrigger value="anomalies">Recent Anomalies</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">System Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Overall Health</span>
                    <Badge variant={status?.is_healthy ? 'secondary' : 'destructive'}>
                      {status?.is_healthy ? 'Healthy' : 'Unhealthy'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Active Controls</span>
                    <Badge variant={controls.length > 0 ? 'destructive' : 'secondary'}>
                      {controls.length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Recent Anomalies</span>
                    <Badge variant={anomalies.length > 0 ? 'default' : 'secondary'}>
                      {anomalies.length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Activity className="h-4 w-4 mr-2" />
                    View System Logs
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Mail className="h-4 w-4 mr-2" />
                    Check Queue Health
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" />
                    Workspace Analytics
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="controls" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Safety Controls</CardTitle>
            </CardHeader>
            <CardContent>
              {controls.length > 0 ? (
                <div className="space-y-4">
                  {controls.map((control) => (
                    <div key={control.id} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="destructive">
                          {control.type.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {control.target_id && (
                          <Badge variant="outline">
                            Target: {control.target_id}
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm font-medium">Reason:</div>
                        <div className="text-sm text-muted-foreground">{control.reason}</div>
                        <div className="text-xs text-muted-foreground">
                          Created: {new Date(control.created_at).toLocaleString()} by {control.created_by}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <div>No active safety controls</div>
                  <div className="text-sm">All email operations are running normally</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="anomalies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Safety Anomalies (24 hours)</CardTitle>
            </CardHeader>
            <CardContent>
              {anomalies.length > 0 ? (
                <div className="space-y-4">
                  {anomalies.map((anomaly, index) => (
                    <Alert
                      key={index}
                      variant={anomaly.severity === 'critical' || anomaly.severity === 'high' ? 'destructive' : 'default'}
                    >
                      {getSeverityIcon(anomaly.severity)}
                      <AlertDescription>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{anomaly.message}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {new Date(anomaly.created_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getSeverityColor(anomaly.severity)}>
                              {anomaly.severity.toUpperCase()}
                            </Badge>
                            {anomaly.requires_action && (
                              <Badge variant="outline" className="text-xs">
                                Action Required
                              </Badge>
                            )}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <div>No recent anomalies detected</div>
                  <div className="text-sm">System is operating within normal parameters</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
